import { useMemo } from 'react';
import { getTableElementConfig } from '../../utils/questionUtils';
import RichTextBlockEleve from './RichTextBlockEleve';
import QCMRenderer from './renderers/QCMRenderer';
import TexteLibreRenderer from './renderers/TexteLibreRenderer';
import RelierRenderer from './renderers/RelierRenderer';
import OrdonnerRenderer from './renderers/OrdonnerRenderer';
import TexteTrouRenderer from './renderers/TexteTrouRenderer';
import ClasserRenderer from './renderers/ClasserRenderer';
import PlacerPointsRenderer from './renderers/PlacerPointsRenderer';
import ProgrammeConstructionRenderer from './renderers/ProgrammeConstructionRenderer';
import HorlogeAiguillesRenderer from './renderers/HorlogeAiguillesRenderer';
import TableResponseElementEleve from '../VueEvalNat/BilanEval/FormBilanEval/FormQuestion/Elements/TableResponseElementEleve';
import Repere2DRenderer from './renderers/Repere2DRenderer';

/**
 * Aperçu complet d'un exercice au FORMAT UNIFIÉ (énoncé + réponses, thème
 * light, bonnes réponses visibles) — le rendu des cartes de l'exerciseur.
 *
 * Extrait d'ExerciseurCard (2.4c) pour être partagé avec les cartes de la
 * banque EvalNat (QuestionCardEvalNat) : toute question adaptée via
 * buildPreviewContenu / toPayloadUnifie se rend ici à l'identique.
 *
 * @param {Object} question - payload unifié : { type|type_question, texte,
 *   contenu, elements_additionnels, bonne_reponse? }
 */
const ExercicePreview = ({ question }) => {
    const typeQuestion = question.type_question || question.type || 'qcm';
    const elements = question.elements_additionnels?.elements || [];
    const contenu = question.contenu || {};

    const tableConfig = useMemo(() => getTableElementConfig({
        ...question,
        bonne_reponse: question.bonne_reponse || contenu.bonne_reponse,
    }) || null, [question, contenu.bonne_reponse]);

    // La table de réponse est rendue par TableResponseElementEleve — on
    // l'exclut de l'énoncé pour ne pas l'afficher deux fois.
    const enonceElements = tableConfig ? elements.filter(el => el.type !== 'table') : elements;

    return (
        // .exercice-preview : remontée typo scopée (index.css) — Nunito rend
        // les petits corps moins lisibles dans les aperçus.
        <div className="space-y-3 exercice-preview">
            {/* Énoncé */}
            <RichTextBlockEleve texte={question.texte} elements={enonceElements} theme="light" imageMaxWidth="compact" />

            {/* Réponses — routing identique à QuestionPreviewModal */}
            {tableConfig && (
                <TableResponseElementEleve config={tableConfig} reponseExistante={contenu.bonne_reponse || ''} readOnly light compact />
            )}

            {(typeQuestion === 'qcm' || typeQuestion === 'qcm_multiple') && !tableConfig && (
                <QCMRenderer choix={contenu.choix} bonneReponse={contenu.bonne_reponse} multi={contenu.multi} light />
            )}

            {typeQuestion === 'texte' && (
                <TexteLibreRenderer bonneReponse={contenu.bonne_reponse} strict={contenu.strict} suffixe={contenu.suffixe} light />
            )}

            {typeQuestion === 'association' && contenu.subType === 'relier' && (
                <RelierRenderer pairs={contenu.pairs} ciblesLeurres={contenu.cibles_leurres} light />
            )}

            {typeQuestion === 'association' && contenu.subType === 'ordonner' && (
                <OrdonnerRenderer items={contenu.items} light />
            )}

            {typeQuestion === 'association' && contenu.subType === 'texte_trou' && (
                <TexteTrouRenderer phrase={contenu.phrase} mode={contenu.mode} distracteurs={contenu.distracteurs} light />
            )}

            {typeQuestion === 'association' && contenu.subType === 'classer' && (
                <ClasserRenderer categories={contenu.categories} light />
            )}

            {typeQuestion === 'association' && contenu.subType === 'placer_points' && (
                <PlacerPointsRenderer numberline={contenu.numberline} bonneReponse={contenu.bonne_reponse} />
            )}

            {typeQuestion === 'association' && contenu.subType === 'placer_points_2d' && (
                <Repere2DRenderer repere={contenu.repere} bonneReponse={contenu.bonne_reponse} dejaPlaces={contenu.dejaPlaces || []} decor={contenu.decor || null} relier={contenu.relier || null} light />
            )}

            {typeQuestion === 'association' && contenu.subType === 'placer_aiguilles' && (
                <HorlogeAiguillesRenderer reponse={contenu.bonne_reponse} avecTrotteuse={!!contenu.avecTrotteuse} />
            )}

            {typeQuestion === 'association' && contenu.subType === 'programme_construction' && (
                <ProgrammeConstructionRenderer
                    viewBox={contenu.viewBox}
                    points={contenu.points || []}
                    preShapes={contenu.preShapes || []}
                    tasks={contenu.tasks || []}
                    pxPerCm={contenu.pxPerCm}
                    bonneReponse={contenu.bonne_reponse}
                />
            )}
        </div>
    );
};

export default ExercicePreview;
